import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Loader2, FlaskConical, Download, Trash2, Search, Wand2, FileText, Send } from "lucide-react";
import { Input } from "@/components/ui/input";
import { FoodSearchPanel } from "@/components/site/FoodSearchPanel";
import { DIETARY_STYLES, HEALTH_CONDITIONS, ChipSelect } from "@/components/admin/DietOptions";

const GOALS = [
  ["fat_loss", "Fat Loss"], ["muscle_build", "Muscle Build"], ["maintenance", "Maintenance"],
  ["general_wellness", "General Wellness"], ["performance", "Performance / Training"], ["custom", "Custom…"],
];
const inputCls = "bg-[#F7F5F0] border-[#E5E2D9] h-10 text-sm";
const areaCls = "w-full bg-[#F7F5F0] border border-[#E5E2D9] rounded-xl px-3 py-2.5 text-sm resize-none";

const MacroCalc = ({ api }) => {
  const search = async (q) => {
    const r = await api().get(`/admin/foods/search?q=${encodeURIComponent(q)}`);
    return r.data.results;
  };
  return (
    <div className="bg-white border border-[#E5E2D9] rounded-2xl p-5" data-testid="studio-macro-calc">
      <div className="font-medium text-sm flex items-center gap-2"><Search size={14} className="text-[#89937C]" /> FatSecret Macro Calculator</div>
      <p className="text-[10px] text-[#9AA294] mt-1 mb-2">Verify any food's calories & macros at YOUR portion size while you curate.</p>
      <FoodSearchPanel search={search} placeholder="e.g. basmati rice cooked" hint="Type a food, then adjust grams/servings — macros recalculate instantly." />
    </div>
  );
};

const PlanView = ({ plan, targets }) => (
  <div data-testid="studio-plan-view">
    <div className="font-serif-display text-2xl">{plan.title}</div>
    <div className="mt-2 flex flex-wrap gap-1.5 text-[11px] font-semibold">
      <span className="rounded-full bg-[#C57B57]/15 text-[#AD6A48] px-2.5 py-1">{plan.calorie_target || targets?.target_kcal} kcal/day</span>
      {plan.macro_split && (
        <>
          <span className="rounded-full bg-[#2D372B]/10 text-[#2D372B] px-2.5 py-1">P {plan.macro_split.protein_g}g</span>
          <span className="rounded-full bg-[#89937C]/15 text-[#5C665A] px-2.5 py-1">C {plan.macro_split.carbs_g}g</span>
          <span className="rounded-full bg-[#89937C]/15 text-[#5C665A] px-2.5 py-1">F {plan.macro_split.fat_g}g</span>
        </>
      )}
      {targets?.bmr && <span className="rounded-full bg-[#F7F5F0] border border-[#E5E2D9] text-[#5C665A] px-2.5 py-1">BMR {targets.bmr} · TDEE {targets.tdee}</span>}
    </div>
    {(plan.health_flags || []).length > 0 && (
      <div className="mt-3 rounded-xl border-2 border-[#C57B57]/50 bg-[#C57B57]/10 p-3 text-xs text-[#7A4A33]" data-testid="studio-health-flags">
        <b>Health flags:</b> {plan.health_flags.join(" · ")}
      </div>
    )}
    {(plan.guideline_compliance || []).length > 0 && (
      <div className="mt-3 rounded-xl bg-[#F7F5F0] p-3" data-testid="studio-compliance">
        <div className="text-[10px] font-bold tracking-[0.15em] text-[#C57B57] uppercase mb-1">Guideline compliance</div>
        {plan.guideline_compliance.map((g, i) => (
          <div key={i} className="text-[11px] text-[#5C665A] mb-1"><b>{g.organisation}</b>: {g.guideline} — <i>{g.how_applied}</i></div>
        ))}
      </div>
    )}
    <div className="mt-4 space-y-4 max-h-[28rem] overflow-y-auto pr-1">
      {(plan.days || []).map((d) => (
        <div key={d.day} className="border border-[#E5E2D9] rounded-xl overflow-hidden">
          <div className="bg-[#2D372B] text-[#F7F5F0] px-3 py-1.5 text-xs font-semibold flex justify-between">
            <span>Day {d.day}{d.cuisine ? <span className="text-[#E8C9B4]"> — {d.cuisine}</span> : null}</span>
            {d.totals && <span>{d.totals.kcal} kcal · P {d.totals.protein}g · C {d.totals.carbs}g · F {d.totals.fat}g</span>}
          </div>
          <table className="w-full text-[11px]">
            <tbody>
              {(d.meals || []).map((m, i) => (
                <tr key={i} className={i % 2 ? "bg-[#FAF9F4]" : "bg-white"}>
                  <td className="px-2 py-1.5 font-semibold text-[#2D372B] whitespace-nowrap align-top">{m.meal}</td>
                  <td className="px-2 py-1.5 text-[#5C665A]">{m.food}{m.quantity ? ` — ${m.quantity}` : ""}{m.notes ? ` (${m.notes})` : ""}</td>
                  <td className="px-2 py-1.5 text-right text-[#5C665A] whitespace-nowrap align-top">{m.kcal} kcal · {m.protein}/{m.carbs}/{m.fat}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ))}
    </div>
    {plan.grocery && Object.keys(plan.grocery).length > 0 && (
      <div className="mt-3 rounded-xl bg-[#F7F5F0] p-3" data-testid="studio-grocery">
        <div className="text-[10px] font-bold tracking-[0.15em] text-[#C57B57] uppercase mb-1">Grocery list — full plan</div>
        {Object.entries(plan.grocery).map(([cat, items]) => (
          <div key={cat} className="text-[11px] text-[#5C665A] mb-1"><b>{cat}</b>: {(items || []).join(" · ")}</div>
        ))}
      </div>
    )}
    {(plan.rotation || []).length > 0 && (
      <div className="mt-3 rounded-xl bg-[#F7F5F0] p-3" data-testid="studio-rotation">
        <div className="text-[10px] font-bold tracking-[0.15em] text-[#C57B57] uppercase mb-1">Rotation — full duration</div>
        {plan.rotation.map((r, i) => <div key={i} className="text-[11px] text-[#5C665A] mb-1">{typeof r === "string" ? r : <><b>{r.segment}</b>: {r.instruction}</>}</div>)}
      </div>
    )}
    {(plan.hydration || plan.supplements_note || plan.coach_notes) && (
      <div className="mt-3 text-[11px] text-[#5C665A] space-y-1">
        {plan.hydration && <div><b>Hydration:</b> {plan.hydration}</div>}
        {plan.supplements_note && <div><b>Supplements:</b> {plan.supplements_note}</div>}
        {plan.coach_notes && <div><b>Coach notes:</b> {plan.coach_notes}</div>}
      </div>
    )}
  </div>
);

const STUDIO_CUISINES = [
  "Indian (Pan-Indian)", "North Indian", "South Indian", "East Indian", "West Indian", "North-East Indian",
  "Ladakhi", "Tibetan", "Chinese",
  "Navratri (vrat/fasting-friendly)", "Ramadan (Suhoor & Iftar)", "Christmas (festive lean)",
  "American", "African", "Middle Eastern", "Mediterranean", "Latin", "Japanese",
];

export const DietStudio = ({ api }) => {
  const [f, setF] = useState({
    client_name: "", age: "", sex: "", height_cm: "", weight_kg: "", activity: "moderate",
    goal: "fat_loss", custom_goal: "", duration_days: 7, weight_basis: "cooked",
    diet_type: "", cuisine: "", target_kcal: "", protein_g: "", carbs_g: "", fat_g: "", fibre_g: "", assessment: "", health_conditions: "", notes: "",
  });
  const [styles, setStyles] = useState([]);
  const [conds, setConds] = useState([]);
  const [allergy, setAllergy] = useState("");
  const [busy, setBusy] = useState(false);
  const [progress, setProgress] = useState(null);
  const [cuisines, setCuisines] = useState([]);
  const [result, setResult] = useState(null);
  const [history, setHistory] = useState(null);
  const [clients, setClients] = useState([]);
  const [sendTo, setSendTo] = useState("");
  const [sending, setSending] = useState(false);
  const set = (k) => (e) => setF({ ...f, [k]: e.target?.value ?? e });

  const loadHistory = () => api().get("/admin/diet/studio/plans").then((r) => setHistory(r.data)).catch(() => setHistory([]));
  useEffect(() => {
    loadHistory();
    api().get("/admin/diet/clients").then((r) => setClients(r.data)).catch(() => {});
  }, []); // eslint-disable-line

  const sendToClient = async () => {
    if (!sendTo) { toast.error("Pick a client first"); return; }
    setSending(true);
    try {
      const r = await api().post(`/admin/diet/studio/plans/${result.id}/send-to-client`, { client_id: sendTo });
      toast.success(`"${r.data.title}" sent to client's dashboard — email + WhatsApp notified`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not send");
    } finally { setSending(false); }
  };

  const generate = async () => {
    if (!f.assessment.trim() && !(f.age && f.weight_kg)) { toast.error("Paste an assessment or at least fill age + weight"); return; }
    setBusy(true);
    try {
      const r = await api().post("/admin/diet/studio/generate", {
        ...f,
        age: f.age ? parseFloat(f.age) : null,
        height_cm: f.height_cm ? parseFloat(f.height_cm) : null,
        weight_kg: f.weight_kg ? parseFloat(f.weight_kg) : null,
        target_kcal: f.target_kcal ? parseFloat(f.target_kcal) : null,
        protein_g: f.protein_g ? parseFloat(f.protein_g) : null,
        carbs_g: f.carbs_g ? parseFloat(f.carbs_g) : null,
        fat_g: f.fat_g ? parseFloat(f.fat_g) : null,
        fibre_g: f.fibre_g ? parseFloat(f.fibre_g) : null,
        cuisines,
        dietary_styles: styles, health_conditions_list: conds, allergy_note: allergy,
        duration_days: parseInt(f.duration_days, 10),
      }, { timeout: 60000 });
      let rec = r.data;
      loadHistory();
      while (rec.status === "generating") {
        setProgress(rec.progress);
        await new Promise((res) => setTimeout(res, 8000));
        rec = (await api().get(`/admin/diet/studio/plans/${rec.id}`)).data;
      }
      if (rec.status === "failed") throw new Error(rec.error || "Generation failed — try again");
      setResult(rec);
      loadHistory();
      toast.success("Plan curated — every day written out in full. Review, then download PDF/Word");
    } catch (e) {
      toast.error(e?.response?.data?.detail || e.message || "Generation failed — try again");
    } finally { setBusy(false); setProgress(null); }
  };

  const downloadFile = async (id, name, fmt) => {
    try {
      const r = await api().get(`/admin/diet/studio/plans/${id}/${fmt}`, { responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `JAY_Diet_Plan_${(name || "Client").replace(/[^A-Za-z0-9]+/g, "_")}.${fmt}`;
      a.click();
      URL.revokeObjectURL(url);
    } catch { toast.error(`Could not download ${fmt.toUpperCase()} file`); }
  };
  const downloadDocx = (id, name) => downloadFile(id, name, "docx");

  const remove = async (id) => {
    if (!window.confirm("Delete this saved plan?")) return;
    try { await api().delete(`/admin/diet/studio/plans/${id}`); if (result?.id === id) setResult(null); loadHistory(); } catch { toast.error("Delete failed"); }
  };

  return (
    <div data-testid="diet-studio">
      <div className="mb-4">
        <div className="font-serif-display text-2xl tracking-tight flex items-center gap-2"><FlaskConical size={20} className="text-[#C57B57]" /> Diet Studio</div>
        <p className="text-sm text-[#5C665A]">Paste any client assessment → AI curates a guideline-compliant plan with exact calories, macros & {"raw/cooked"} weights → download as Word.</p>
      </div>

      <div className="grid lg:grid-cols-2 gap-5 items-start">
        <div className="space-y-4">
          <div className="bg-white border border-[#E5E2D9] rounded-2xl p-5 space-y-3">
            <div className="grid grid-cols-2 gap-2">
              <Input data-testid="studio-name" placeholder="Client name" value={f.client_name} onChange={set("client_name")} className={inputCls} />
              <select data-testid="studio-sex" value={f.sex} onChange={set("sex")} className="bg-[#F7F5F0] border border-[#E5E2D9] rounded-md h-10 px-2 text-sm">
                <option value="">Sex</option><option>Male</option><option>Female</option>
              </select>
              <Input data-testid="studio-age" type="number" placeholder="Age" value={f.age} onChange={set("age")} className={inputCls} />
              <Input data-testid="studio-height" type="number" placeholder="Height (cm)" value={f.height_cm} onChange={set("height_cm")} className={inputCls} />
              <Input data-testid="studio-weight" type="number" placeholder="Weight (kg)" value={f.weight_kg} onChange={set("weight_kg")} className={inputCls} />
              <select data-testid="studio-activity" value={f.activity} onChange={set("activity")} className="bg-[#F7F5F0] border border-[#E5E2D9] rounded-md h-10 px-2 text-sm">
                <option value="sedentary">Sedentary</option><option value="light">Lightly active</option>
                <option value="moderate">Moderately active</option><option value="active">Very active</option><option value="athlete">Athlete</option>
              </select>
            </div>
            <div className="grid grid-cols-2 gap-2">
              <select data-testid="studio-goal" value={f.goal} onChange={set("goal")} className="bg-[#F7F5F0] border border-[#E5E2D9] rounded-md h-10 px-2 text-sm">
                {GOALS.map(([id, label]) => <option key={id} value={id}>{label}</option>)}
              </select>
              <select data-testid="studio-duration" value={f.duration_days} onChange={set("duration_days")} className="bg-[#F7F5F0] border border-[#E5E2D9] rounded-md h-10 px-2 text-sm">
                <option value={7}>7 days</option><option value={15}>15 days</option><option value={30}>30 days</option>
              </select>
            </div>
            {f.goal === "custom" && <Input data-testid="studio-custom-goal" placeholder="Describe the training need (e.g. marathon prep, cutting for competition)" value={f.custom_goal} onChange={set("custom_goal")} className={inputCls} />}
            <div className="flex items-center gap-2 text-xs">
              <span className="text-[#5C665A] font-medium">Quantities:</span>
              {["cooked", "raw"].map((b) => (
                <button key={b} type="button" data-testid={`studio-basis-${b}`} onClick={() => setF({ ...f, weight_basis: b })}
                  className={`rounded-full px-4 py-1.5 border font-medium ${f.weight_basis === b ? "bg-[#2D372B] text-white border-[#2D372B]" : "bg-white border-[#E5E2D9] text-[#5C665A]"}`}>
                  {b === "cooked" ? "Cooked weights" : "Raw weights"}
                </button>
              ))}
            </div>
            <div className="grid grid-cols-3 gap-2">
              <Input placeholder="Diet type (veg…)" value={f.diet_type} onChange={set("diet_type")} className={inputCls} />
              <Input placeholder="Other cuisine (free text)" value={f.cuisine} onChange={set("cuisine")} className={inputCls} />
              <Input data-testid="studio-kcal" type="number" placeholder="kcal override" value={f.target_kcal} onChange={set("target_kcal")} className={inputCls} />
            </div>
            <div data-testid="studio-cuisine-multi">
              <div className="text-[10px] font-bold tracking-[0.15em] text-[#C57B57] uppercase mb-1.5">Cuisines — pick one or many (mixed-cuisine plan across days)</div>
              <ChipSelect options={STUDIO_CUISINES} value={cuisines} onChange={setCuisines} testPrefix="st-cuisine" />
              {cuisines.length > 1 && <p className="mt-1.5 text-[10px] text-[#5C665A]">{cuisines.length} cuisines selected — the plan will rotate them across the days (Day 1 {cuisines[0]}, Day 2 {cuisines[1]}…), each day labelled with its cuisine.</p>}
            </div>
            <div className="grid grid-cols-4 gap-2">
              <Input data-testid="studio-protein" type="number" placeholder="Protein g" value={f.protein_g} onChange={set("protein_g")} className={inputCls} />
              <Input data-testid="studio-carbs" type="number" placeholder="Carbs g" value={f.carbs_g} onChange={set("carbs_g")} className={inputCls} />
              <Input data-testid="studio-fat" type="number" placeholder="Fat g" value={f.fat_g} onChange={set("fat_g")} className={inputCls} />
              <Input data-testid="studio-fibre" type="number" placeholder="Fibre g" value={f.fibre_g} onChange={set("fibre_g")} className={inputCls} />
            </div>
            <div data-testid="studio-styles">
              <div className="text-[10px] font-bold tracking-[0.15em] text-[#C57B57] uppercase mb-1.5">Dietary style / eating pattern</div>
              <ChipSelect options={DIETARY_STYLES} value={styles} onChange={setStyles} testPrefix="st-style" />
            </div>
            <div data-testid="studio-condition-chips">
              <div className="text-[10px] font-bold tracking-[0.15em] text-[#C57B57] uppercase mb-1.5">Health conditions (select all that apply)</div>
              <ChipSelect options={HEALTH_CONDITIONS} value={conds} onChange={setConds} testPrefix="st-cond" />
              {conds.some((c) => c.includes("Allergy")) && (
                <Input data-testid="studio-allergy" placeholder="Mention the exact food(s) the client is allergic to…" value={allergy}
                  onChange={(e) => setAllergy(e.target.value)} className="mt-2 bg-[#F7F5F0] border-[#E5E2D9] h-9 text-xs" />
              )}
            </div>
            <textarea data-testid="studio-assessment" rows={6} placeholder="Paste the client's assessment here — any format works (answers collected on WhatsApp, email, paper forms…)" value={f.assessment} onChange={set("assessment")} className={areaCls} />
            <textarea data-testid="studio-conditions" rows={2} placeholder="Health conditions (diabetes, CKD, hypertension, thyroid, PCOS…) — the plan will follow ICMR / KDIGO / USDA / NIH / WHO guidance" value={f.health_conditions} onChange={set("health_conditions")} className={areaCls} />
            <textarea rows={2} placeholder="Your coaching notes / special instructions (optional)" value={f.notes} onChange={set("notes")} className={areaCls} />
            <button data-testid="studio-generate" onClick={generate} disabled={busy}
              className="w-full inline-flex items-center justify-center gap-2 rounded-full px-6 py-3 bg-[#C57B57] text-white text-sm font-medium hover:bg-[#AD6A48] disabled:opacity-60">
              {busy ? <><Loader2 size={14} className="animate-spin" /> {progress?.done ? `Curating — day ${progress.done} of ${progress.total} done…` : "Curating plan… (larger plans build day-by-day)"}</> : <><Wand2 size={14} /> Curate Diet Plan</>}
            </button>
          </div>
          <MacroCalc api={api} />
        </div>

        <div className="space-y-4">
          <div className="bg-white border border-[#E5E2D9] rounded-2xl p-5">
            {!result ? (
              <p className="text-sm text-[#9AA294] py-10 text-center" data-testid="studio-empty">Your curated plan will appear here — with calorie targets, macro break-up, day-by-day meals and guideline compliance.</p>
            ) : (
              <>
                <div className="flex flex-wrap justify-end items-center gap-2 mb-2">
                  <button data-testid="studio-pdf-btn" onClick={() => downloadFile(result.id, result.client_name, "pdf")}
                    className="inline-flex items-center gap-1.5 rounded-full px-4 py-2 border border-[#2D372B]/30 text-[#2D372B] text-xs font-medium hover:bg-[#2D372B]/5">
                    <Download size={12} /> PDF
                  </button>
                  <button data-testid="studio-docx-btn" onClick={() => downloadDocx(result.id, result.client_name)}
                    className="inline-flex items-center gap-1.5 rounded-full px-4 py-2 bg-[#2D372B] text-white text-xs font-medium hover:bg-[#89937C]">
                    <Download size={12} /> Word (.docx)
                  </button>
                </div>
                {clients.length > 0 && (
                  <div className="mb-3 flex flex-wrap items-center gap-2 rounded-xl bg-[#F7F5F0] px-3 py-2" data-testid="studio-send-row">
                    <span className="text-[11px] font-semibold text-[#5C665A]">Send to client's dashboard:</span>
                    <select data-testid="studio-send-select" value={sendTo} onChange={(e) => setSendTo(e.target.value)}
                      className="bg-white border border-[#E5E2D9] rounded-lg h-8 px-2 text-xs max-w-[180px]">
                      <option value="">Choose client…</option>
                      {clients.map((c) => <option key={c.id} value={c.id}>{c.name} ({c.email})</option>)}
                    </select>
                    <button data-testid="studio-send-btn" onClick={sendToClient} disabled={sending}
                      className="inline-flex items-center gap-1 rounded-full px-4 py-1.5 bg-[#C57B57] text-white text-[10px] font-medium hover:bg-[#AD6A48] disabled:opacity-60">
                      {sending ? <Loader2 size={11} className="animate-spin" /> : <Send size={11} />} Send (PDF + notify)
                    </button>
                  </div>
                )}
                <PlanView plan={result.plan} targets={result.targets} />
              </>
            )}
          </div>

          <div className="bg-white border border-[#E5E2D9] rounded-2xl p-5" data-testid="studio-history">
            <div className="font-medium text-sm mb-2 flex items-center gap-2"><FileText size={14} className="text-[#89937C]" /> Saved plans</div>
            {!history ? <Loader2 size={14} className="animate-spin text-[#89937C]" /> : history.length === 0 ? (
              <p className="text-xs text-[#9AA294]">No plans yet.</p>
            ) : (
              <ul className="space-y-1.5">
                {history.map((h) => (
                  <li key={h.id} className="flex items-center justify-between gap-2 text-xs text-[#5C665A] border-b border-[#F0EEE6] pb-1.5">
                    <button className="text-left min-w-0 truncate hover:text-[#2D372B]" onClick={() => api().get(`/admin/diet/studio/plans/${h.id}`).then((r) => r.data.plan ? setResult(r.data) : toast.error(r.data.status === "generating" ? "Still generating — check back shortly" : "This plan failed — please regenerate"))}>
                      <b>{h.client_name || "Client"}</b> · {h.custom_goal || h.goal} · {h.duration_days}d · {new Date(h.created_at).toLocaleDateString()}
                      {h.status === "generating" && <span className="text-[#C57B57] font-semibold"> · generating{h.progress?.done ? ` (day ${h.progress.done}/${h.progress.total})` : "…"}</span>}
                      {h.status === "failed" && <span className="text-[#A05252] font-semibold"> · failed</span>}
                    </button>
                    <span className="shrink-0 flex gap-2">
                      <button data-testid={`studio-dl-${h.id}`} onClick={() => downloadDocx(h.id, h.client_name)} className="text-[#2D372B] hover:opacity-70"><Download size={13} /></button>
                      <button data-testid={`studio-del-${h.id}`} onClick={() => remove(h.id)} className="text-[#A05252] hover:opacity-70"><Trash2 size={13} /></button>
                    </span>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};
